<template>
  <ModuleWrapper
    :moduleId="moduleId"
    class="tw-relative"
    :title="moduleId === 'stageFont' ? 'Stage Font' : 'Event Landing Font'"
    @initializeData="initializeData"
  >
    <template #main>
      <div class="tw-mb-2 tw-block tw-text-xs">
        Select a web compatible font that has Regular, Medium and Bold weights
      </div>
      <div v-show="moduleId === 'stageFont'" class="tw-mb-4 tw-flex tw-h-7 tw-items-center tw-justify-between">
        <div class="tw-text-xs">Use same font and color as event landing</div>
        <StudioToggle
          id="stage-font-event-landing-toggle"
          :checked="!!hideFontDropdown"
          @update:checked="toggleFontDropdown"
        />
      </div>
      <div v-show="moduleId === 'landingFont' || hideFontDropdown === false">
        <ConditionalColorPicker
          id="font-color-picker"
          label="Font Color"
          type="color"
          class="tw-mb-4"
          :initialColor="desktopColor.style"
          @colorChange="onFontColorChange"
        />
        <FontDropdown
          :selectedFontOption="selectedFontOption"
          :moduleId="moduleId"
          :hideFontDropdown="hideFontDropdown"
          class="tw-mb-4"
          @setSelectedFontOption="setSelectedFontOption"
        />
        <div v-show="selectedFontOption === 'Web Font'">
          <StudioInput
            id="web-font-name-input"
            ref="webFontNameInput"
            :modelValue="custom_font_name"
            required
            label="Font Name"
            class="tw-mb-4"
            :inlineError="fontNameInvalidError"
            @update:modelValue="handleUpdateFontName"
          />
          <StudioInput
            id="web-font-url-input"
            ref="webFontUrlInput"
            :modelValue="theme_font_link"
            required
            label="Font URL"
            placeholder="https://..."
            :inlineError="fontUrlInvalidError"
            @update:modelValue="handleUpdateFontURL"
          />
        </div>
        <div v-show="selectedFontOption === 'Custom'">
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <a
              v-if="themeFontSrc"
              :href="themeFontSrc"
              target="_blank"
              class="tw-w-[300px] tw-overflow-hidden tw-overflow-ellipsis tw-text-sm"
            >
              {{ customFontName }}
            </a>
            <FilestackS3
              :s3path="s3BucketPath"
              :acceptedTypes="[
                '.ttf',
                '.otf',
                '.woff',
                '.woff2',
                'TrueType',
                'font/otf',
                'font/sfnt',
                'font/ttf',
                'font/woff',
                'font/woff2',
                'application/vnd.oasis.opendocument.formula-template'
              ]"
              @fileuploaded="onFileUpload"
            >
              <StudioButton trackingId="upload-font-file-button" :variant="themeFontSrc ? 'outline' : 'warn'">
                {{ themeFontSrc ? 'Change File' : 'Upload File' }}
              </StudioButton>
            </FilestackS3>
          </div>
          <div class="tw-mt-2 tw-text-xs tw-text-slate-500">Supported Formats - TTF, OTF, WOFF, WOFF2</div>
        </div>
      </div>
    </template>
  </ModuleWrapper>
</template>

<script>
import throttle from 'lodash.throttle';
import { mapState, mapMutations, mapGetters } from 'vuex';

import ModuleWrapper from '../ModuleWrapper.vue';
import FontDropdown from './FontDropdown.vue';
import { color } from '../../data/defaultValues';

import { GET_RESOURCES_PATH, STATIC_ASSETS } from '@/constants';
import ConditionalColorPicker from '@/studio/lookAndFeel/components/ConditionalColorPicker.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';
import StudioToggle from '@/studioComponents/StudioToggle.vue';
import { showErrorNotification, destructureRgbColor } from '@/utils/utils.js';

export default {
  name: 'FontModule',
  components: {
    ModuleWrapper,
    FontDropdown,
    ConditionalColorPicker,
    StudioToggle,
    StudioInput,
    StudioButton
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`,
      desktopColor: color,
      selectedFontOption: 'Inter',
      custom_font_name: '',
      theme_font_link: '',
      hideFontDropdown: true,
      customThemeFontFilename: '',
      fontNameInvalidError: '',
      fontUrlInvalidError: ''
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapState('branding', ['isCurrentModuleInValid', 'changesMadeInThemeObj']),
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData',
      theme_obj: 'theme_obj'
    }),
    themeType() {
      const moduleKeys = ['theme_type'];
      const moduleData = this.getInitialModuleData(moduleKeys);
      return moduleData.theme_type;
    },
    themeFontSrc() {
      const moduleKeys = ['theme_font_src', 'splash_theme_font_src'];
      const moduleData = this.getInitialModuleData(moduleKeys);

      if (this.moduleId === 'landingFont') {
        return moduleData.splash_theme_font_src;
      }
      return moduleData.theme_font_src;
    },
    customFontName() {
      if (!this.themeFontSrc) {
        return '';
      }

      if (this.customThemeFontFilename) {
        return this.customThemeFontFilename;
      }

      const url = decodeURIComponent(this.themeFontSrc);
      const rawFileName = url.split('/').pop();
      const prefix = rawFileName.split('_')[0];
      const fileName = rawFileName.split(prefix + '_')[1];
      return fileName || 'Custom Font';
    },
    isFormValid() {
      if (this.hideFontDropdown) {
        return true;
      }

      if (this.selectedFontOption == 'Web Font') {
        return !this.fontNameInvalidError && !this.fontUrlInvalidError;
      }
      if (this.selectedFontOption == 'Custom') {
        return this.themeFontSrc;
      }
      return true;
    }
  },
  watch: {
    isFormValid(isValid) {
      this.setCurrentModuleInvalid(!isValid);
    },
    themeType() {
      this.setSelectedFontOption('Inter');
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = [
        'primary_text_color',
        'theme_font_name',
        'theme_font_link',
        'font_type',
        'custom_font_name',
        'splash_text_color',
        'splash_theme_font_name',
        'splash_theme_font_link',
        'splash_font_type',
        'splash_custom_font_name',
        'splash_theme_font_src',
        'theme_font_src'
      ];
      const {
        primary_text_color,
        custom_font_name,
        theme_font_link,
        font_type,
        theme_font_name,
        splash_text_color,
        splash_theme_font_name,
        splash_theme_font_link,
        splash_font_type,
        splash_custom_font_name,
        splash_theme_font_src,
        theme_font_src
      } = this.getInitialModuleData(moduleKeys);

      // check if event landing and stage fonts are same
      // for doing that check if any of the below conditions are true (using or statements)
      // splash_text_color equals primary_text_color
      // custom_font_name equals splash_custom_font_name
      // theme_font_link equals splash_theme_font_link
      // font_type is Custom, splash_font_type is custom and theme_font_src equals splash_theme_font_src
      // theme_font_name == splash_theme_font_name
      const isEventAndStageFontSame =
        (primary_text_color === splash_text_color || splash_text_color === 'var(--primary-text-color)') &&
        custom_font_name === splash_custom_font_name &&
        theme_font_link === splash_theme_font_link &&
        (font_type === splash_font_type ||
          (font_type === 'Custom' &&
            splash_font_type === 'Custom Splash' &&
            splash_theme_font_src === theme_font_src)) &&
        theme_font_name === splash_theme_font_name;

      if (isEventAndStageFontSame) {
        this.hideFontDropdown = true;
      } else {
        this.hideFontDropdown = false;
      }

      if (this.moduleId === 'landingFont') {
        splash_text_color === 'var(--primary-text-color)'
          ? (this.desktopColor = destructureRgbColor(primary_text_color))
          : (this.desktopColor = destructureRgbColor(splash_text_color));
        this.custom_font_name = splash_custom_font_name;
        this.theme_font_link = splash_theme_font_link;
        if (splash_font_type === 'Default' || splash_font_type === 'Inter' || splash_font_type === 'Web Safe Font') {
          this.selectedFontOption =
            splash_theme_font_name || (splash_font_type === 'Default' ? 'Default-GT-America' : 'Inter');
        } else {
          this.selectedFontOption = splash_font_type === 'Custom Splash' ? 'Custom' : splash_font_type;
        }
      } else {
        this.desktopColor = destructureRgbColor(primary_text_color);
        this.custom_font_name = custom_font_name;
        this.theme_font_link = theme_font_link;
        if (font_type === 'Default' || font_type === 'Inter' || font_type === 'Web Safe Font') {
          this.selectedFontOption = theme_font_name || (font_type === 'Default' ? 'Default-GT-America' : 'Inter');
        } else {
          this.selectedFontOption = font_type;
        }
      }
    },
    handleUpdateFontName(value) {
      this.custom_font_name = value;
      this.validateFontName();
    },
    handleUpdateFontURL(value) {
      this.theme_font_link = value;
      this.validateFontUrl();
    },
    onFontColorChange(color) {
      this.desktopColor = color;

      if (this.moduleId === 'landingFont') {
        if (this.hideFontDropdown == true) {
          this.setChangesMadeInThemeObj({
            primary_text_color: color?.style,
            splash_text_color: color?.style
          });
        } else {
          this.setChangesMadeInThemeObj({
            splash_text_color: color?.style
          });
        }
      } else {
        const moduleData = ['splash_text_color'];
        const { splash_text_color } = this.getInitialModuleData(moduleData);
        this.setChangesMadeInThemeObj({
          primary_text_color: color?.style,
          splash_text_color
        });
      }
    },
    validateFontName: throttle(function () {
      if (!this.custom_font_name?.trim()) {
        this.fontNameInvalidError = 'Font name is required';
      } else {
        this.makeChangesInThemeObj();
        this.fontNameInvalidError = '';
      }
    }, 200),
    validateFontUrl: throttle(function () {
      const urlPattern = new RegExp(
        '^(https?:\\/\\/)' + // protocol
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+ -@$]*)*' + // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i'
      );

      if (!this.theme_font_link?.trim()) {
        this.fontUrlInvalidError = 'Font URL is required';
      } else if (!urlPattern.test(this.theme_font_link)) {
        this.fontUrlInvalidError = 'Invalid URL';
      } else {
        this.fontUrlInvalidError = '';
        this.makeChangesInThemeObj();
      }
    }, 200),
    makeChangesInThemeObj() {
      if (this.moduleId === 'landingFont') {
        if (this.hideFontDropdown == true) {
          this.setChangesMadeInThemeObj({
            splash_custom_font_name: this.custom_font_name,
            custom_font_name: this.custom_font_name,
            theme_font_link: this.theme_font_link,
            splash_theme_font_link: this.theme_font_link
          });
        } else {
          this.setChangesMadeInThemeObj({
            splash_custom_font_name: this.custom_font_name,
            splash_theme_font_link: this.theme_font_link
          });
        }
      } else {
        this.setChangesMadeInThemeObj({
          custom_font_name: this.custom_font_name,
          theme_font_link: this.theme_font_link
        });
      }
    },
    onFileUpload(files) {
      const { filesUploaded, filesFailed } = files;
      if (filesFailed?.length !== 0) {
        showErrorNotification('File Upload Failed');
      } else {
        if (filesUploaded?.length > 0) {
          const uploadedFileName = filesUploaded[0]?.originalFile?.name;
          const resourceKey = encodeURIComponent(filesUploaded[0]?.key || '');
          const url = `${STATIC_ASSETS.CLOUDFRONT_URL}/${resourceKey}`;
          this.customThemeFontFilename = uploadedFileName;

          if (this.moduleId === 'landingFont') {
            if (this.hideFontDropdown == true) {
              this.setChangesMadeInThemeObj({
                theme_font_src: url,
                splash_theme_font_src: url
              });
            } else {
              this.setChangesMadeInThemeObj({
                splash_theme_font_src: url
              });
            }
          } else {
            this.setChangesMadeInThemeObj({
              theme_font_src: url
            });
          }
        } else {
          showErrorNotification('File Upload Failed');
        }
      }
    },
    setSelectedFontOption(selectedFontOption) {
      this.selectedFontOption = selectedFontOption;

      if (selectedFontOption === 'Web Font') {
        this.validateFontName();
        this.validateFontUrl();
      }
    },
    toggleFontDropdown() {
      this.hideFontDropdown = !this.hideFontDropdown;
      const moduleKeys = [
        'primary_text_color',
        'splash_text_color',
        'splash_theme_font_name',
        'splash_theme_font_link',
        'splash_font_type',
        'splash_custom_font_name',
        'splash_theme_font_src'
      ];
      const {
        primary_text_color,
        splash_text_color,
        splash_theme_font_name,
        splash_theme_font_link,
        splash_font_type,
        splash_custom_font_name,
        splash_theme_font_src
      } = this.getInitialModuleData(moduleKeys);

      this.desktopColor = destructureRgbColor(primary_text_color);
      this.custom_font_name = splash_custom_font_name;
      this.theme_font_link = splash_theme_font_link;
      if (splash_font_type === 'Default' || splash_font_type === 'Inter' || splash_font_type === 'Web Safe Font') {
        this.selectedFontOption =
          splash_theme_font_name || (splash_font_type === 'Default' ? 'Default-GT-America' : 'Inter');
      } else {
        this.selectedFontOption = splash_font_type === 'Custom Splash' ? 'Custom' : splash_font_type;
      }

      if (this.hideFontDropdown) {
        this.setChangesMadeInThemeObj({
          primary_text_color:
            splash_text_color === 'var(--primary-text-color)' ? primary_text_color : splash_text_color,
          font_type: splash_font_type === 'Custom Splash' ? 'Custom' : splash_font_type,
          theme_font_link: splash_theme_font_link,
          custom_font_name: splash_custom_font_name,
          theme_font_src: splash_theme_font_src,
          theme_font_name: splash_theme_font_name
        });
      }
    }
  }
};
</script>
